import { DateTimeFormatter } from '@wix/bookings-date-time';
import { PriceUtils } from '@wix/bookings-uou-mappers';
import { Experiments } from '@wix/yoshi-flow-editor';
import {
  createBuyPlanBenefitOption,
  createBuyPlanOption,
  createCustomPriceOption,
  createDepositOption,
  createFreeOption,
  createFullAmountOption,
  createPayOfflineOption,
  createPayOnlineOption,
  createSubscriptionOption,
  createUsePlanOption,
  isDepositOrFullAmountFeatureEnabled,
  isOfferedAsOneTime,
} from '..';
import { ExperimentsConsts } from '../../../../consts/experiments';
import {
  PaymentMethod,
  PaymentOption,
  TFunction,
} from '../../../../types/types';
import { isServiceOfferedAsPricingPlan } from '../../../mappers/serviceMapper/utils/payment.mapper';
import { SlotService } from '../../../state/types';

export const getSlotServicePaymentOptions = ({
  slotService,
  numberOfParticipants,
  isPricingPlanInstalled,
  t,
  dateAndTimeFormatter,
  dateRegionalSettingsLocale,
  experiments,
  isCart,
  excludePricingPlanOptions = false,
}: {
  slotService: SlotService;
  numberOfParticipants: number;
  isPricingPlanInstalled: boolean;
  t: TFunction;
  dateAndTimeFormatter: DateTimeFormatter;
  dateRegionalSettingsLocale: string;
  experiments: Experiments;
  isCart: boolean | undefined;
  excludePricingPlanOptions?: boolean;
}): PaymentOption[] => {
  const isUseNewBenefitsAPIUoUEnabled = experiments.enabled(
    ExperimentsConsts.UseNewBenefitsAPIUoU,
  );

  const isSubscription =
    slotService.paymentDetails.subscriptionInfo &&
    experiments.enabled(ExperimentsConsts.SubscriptionPricingUoU);
  if (isSubscription) {
    const formattedPrice = PriceUtils.getFormattedCurrency({
      price: slotService.paymentDetails.price,
      currency: slotService.paymentDetails.currency,
      locale: dateRegionalSettingsLocale,
      currencyDisplay: 'narrowSymbol',
    });
    return [
      createSubscriptionOption(
        t,
        slotService.paymentDetails.subscriptionInfo!.totalPayments,
        formattedPrice,
      ),
    ];
  }

  const options = excludePricingPlanOptions
    ? []
    : slotService.memberships?.eligibleMemberships?.map((membership) =>
        createUsePlanOption(
          membership,
          numberOfParticipants,
          t,
          dateAndTimeFormatter,
          undefined,
          experiments,
        ),
      ) ?? [];

  // add invalid options if at least one option is eligible
  if (
    isUseNewBenefitsAPIUoUEnabled &&
    !excludePricingPlanOptions &&
    options.length > 0 &&
    slotService.memberships?.invalidMemberships?.length
  ) {
    options.push(
      ...slotService.memberships.invalidMemberships
        .filter((membership) => membership.membership?.credits?.remaining !== 0)
        .map((membership) =>
          createUsePlanOption(
            membership.membership!,
            numberOfParticipants,
            t,
            dateAndTimeFormatter,
            membership.reason,
            experiments,
          ),
        ),
    );
  }

  const showBuyAPricingPlan = isServiceOfferedAsPricingPlan(
    slotService.service.payment,
    isPricingPlanInstalled,
  );

  if (
    !excludePricingPlanOptions &&
    options.length === 0 &&
    showBuyAPricingPlan
  ) {
    options.push(
      ...(slotService.possiblePlans?.map((plan) => createBuyPlanOption(plan)) ??
        []),
    );
    options.push(
      ...(slotService.eligiblePlans?.map((plan) =>
        createBuyPlanBenefitOption(plan),
      ) ?? []),
    );
  }
  const showPaySingleSession = isOfferedAsOneTime(slotService.service.payment);

  if (showPaySingleSession) {
    let price = slotService.paymentDetails.price;
    if (slotService.dynamicPriceInfo?.selectedVariants?.length) {
      const selectedVariant = slotService.dynamicPriceInfo?.selectedVariants[0];

      const priceString =
        slotService.dynamicPriceInfo?.serviceOptionsAndVariants?.variants?.values?.find(
          (value) =>
            value.choices?.find(
              (choice) =>
                (choice.custom &&
                  choice.custom === selectedVariant?.choices?.[0].custom) ||
                (choice.staffMemberId &&
                  choice.staffMemberId ===
                    selectedVariant?.choices?.[0].staffMemberId) ||
                (choice.duration &&
                  choice.duration.minutes ===
                    selectedVariant?.choices?.[0].duration?.minutes),
            ),
        )?.price?.value!;

      price = Number(priceString);
    }

    const deposit = slotService.paymentDetails.minCharge;
    const isDepositeOrFullAmountEnabled = isDepositOrFullAmountFeatureEnabled({
      experiments,
      fullUpfrontPaymentAllowed:
        slotService.paymentDetails.fullUpfrontPaymentAllowed,
      deposit,
      paymentTypes: slotService.service.paymentTypes,
    });

    if (isDepositeOrFullAmountEnabled) {
      const formattedPrice = PriceUtils.getFormattedCurrency({
        price,
        currency: slotService.paymentDetails.currency,
        locale: dateRegionalSettingsLocale,
        currencyDisplay: 'narrowSymbol',
      });
      const formattedDeposit = PriceUtils.getFormattedCurrency({
        price: deposit,
        currency: slotService.paymentDetails.currency,
        locale: dateRegionalSettingsLocale,
        currencyDisplay: 'narrowSymbol',
      });
      options.push(createDepositOption(formattedDeposit, t));
      options.push(createFullAmountOption(formattedPrice, t));
    } else if (
      slotService.service.paymentTypes.includes(PaymentMethod.ONLINE)
    ) {
      const formattedPrice = PriceUtils.getFormattedCurrency({
        price,
        currency: slotService.paymentDetails.currency,
        locale: dateRegionalSettingsLocale,
        currencyDisplay: 'narrowSymbol',
      });
      options.push(
        createPayOnlineOption(
          formattedPrice,
          t,
          experiments,
          slotService.paymentDetails.discountRuleName,
          isCart,
        ),
      );
    }
    if (slotService.service.paymentTypes.includes(PaymentMethod.OFFLINE)) {
      const isFree = slotService.service.payment.paymentDetails.isFree;
      const customPrice = slotService.service.payment.paymentDetails.priceText;
      if (isFree) {
        if (customPrice) {
          options.push(createCustomPriceOption(customPrice));
        } else {
          options.push(createFreeOption());
        }
      } else {
        options.push(
          createPayOfflineOption(
            PriceUtils.getFormattedCurrency({
              price,
              currency: slotService.paymentDetails.currency,
              locale: dateRegionalSettingsLocale,
              currencyDisplay: 'narrowSymbol',
            }),
            t,
            experiments,
            slotService.paymentDetails.discountRuleName,
            isCart,
          ),
        );
      }
    }
  }

  return options;
};
